import React from 'react';
import Heading from 'components/Heading';
import Spacing from 'components/Spacing';
import PageSection from 'components/PageSection';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { numberToCurrencyString } from 'further-ui/utils';
import {
  LpFeeCommitmentStatusLabels,
  LpFeeCalculationBasisLabels,
} from 'further-ui/labels';
import { GridAlignment } from '@mui/x-data-grid-pro';
import { FieldRow } from 'components/FormElements';
import { LpFeeCommitmentStatus } from 'further-types/lp/lp-fee-template';
import { CalendarDay } from 'further-ui/utils';
import { sumBy } from 'lodash';

type FeeData = {
  _id: string;
  limitedPartner: string;
  accrualDate: CalendarDay;
  lpFeeTemplateName: string;
  lpFeeTemplateFeeName: string;
  insideOutsideCommitment: LpFeeCommitmentStatus;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
};

interface Vehicle {
  vehicleName: string;
}

interface FeesSummaryDisplayProps {
  vehicle: Vehicle;
  feeData: Array<FeeData>;
  feesDate: CalendarDay;
  title?: string;
  showSummarySection?: boolean;
}

const FeesSummaryDisplay: React.FC<FeesSummaryDisplayProps> = ({
  vehicle,
  feeData,
  feesDate,
  title = 'Fee Breakdown',
  showSummarySection = true,
}) => {
  const feeCount = feeData.length;
  const totalIncTax = sumBy(feeData, 'feeIncTaxLcy');

  const tableRows = feeData.map((fee) => ({
    ...fee,
    id: fee._id,
    limitedPartner: fee.limitedPartner,
    lpFeeTemplateName: fee.lpFeeTemplateName,
    lpFeeTemplateFeeName: fee.lpFeeTemplateFeeName,
    insideOutsideCommitment: fee.insideOutsideCommitment,
  }));

  const leftAlign: GridAlignment = 'left';
  const columns: Array<Column> = [
    {
      headerName: 'Limited partner',
      field: 'limitedPartner',
      flex: 1,
    },
    {
      headerName: 'Accrual date',
      field: 'accrualDate',
      flex: 1,
      renderCell: (params) => params.value.toLabel(),
    },
    {
      headerName: 'Template name',
      field: 'lpFeeTemplateName',
      flex: 1,
    },
    {
      headerName: 'Fee name',
      field: 'lpFeeTemplateFeeName',
      flex: 1,
    },
    {
      headerName: 'Inside/outside commitment',
      field: 'insideOutsideCommitment',
      flex: 1,
      renderCell: (params) => LpFeeCommitmentStatusLabels.get(params.value),
    },
    {
      headerName: 'Calculation basis',
      field: 'calculationBasis',
      flex: 1,
      renderCell: (params) => LpFeeCalculationBasisLabels.get(params.value),
    },
    {
      headerName: 'Fee',
      field: 'feeExTaxLcy',
      flex: 1,
      align: leftAlign,
      headerAlign: leftAlign,
      renderCell: (params) => numberToCurrencyString(params.value),
    },
    {
      headerName: 'Sales tax',
      field: 'salesTaxAmountLcy',
      flex: 1,
      align: leftAlign,
      headerAlign: leftAlign,
      renderCell: (params) => numberToCurrencyString(params.value),
    },
    {
      headerName: 'Total charge',
      field: 'feeIncTaxLcy',
      flex: 1,
      renderCell: (params) => numberToCurrencyString(params.value),
    },
  ];

  return (
    <>
      {showSummarySection && (
        <PageSection title="Summary of fees being processed">
          <Spacing size="sm">
            <FieldRow title="Vehicle">{vehicle?.vehicleName}</FieldRow>
            <FieldRow title="Number of fees">{feeCount}</FieldRow>
            <FieldRow title="Total amount of fees">
              {numberToCurrencyString(totalIncTax)}
            </FieldRow>
            <FieldRow title="Fees date">{feesDate.toLocalDateLabel()}</FieldRow>
          </Spacing>
        </PageSection>
      )}

      <PageSection>
        <Spacing>
          <Heading variant="h3" noMargin>
            {title}
          </Heading>

          <InteractiveTable
            id="fees-summary-table"
            rows={tableRows}
            columns={columns}
            scrollable
          />
        </Spacing>
      </PageSection>
    </>
  );
};

export default FeesSummaryDisplay;
